import {
  capitalCase,
  useRecentInjector,
  useServerPagination,
  useToggle,
  vtrUrl,
} from '@lib/common';
import { EntityType } from '@lib/deals';
import { DataGrid, DataGridProps} from '@material-ui/data-grid';
import React, { useState, useRef, useEffect, useCallback, useMemo} from 'react';
import { map } from 'rxjs/operators';
import { contentToColumns } from './columns';
import * as pintService from '@api/pint';
import * as personService from '@api/person';
import * as contentService from '@api/content';
import clsx from 'clsx';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Link,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Avatar,
  makeStyles,
  createStyles,
  Tooltip,
  TextField,
} from '@material-ui/core';
import { grey, red } from '@material-ui/core/colors';
import SearchBox from '@components/search-box';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LaunchIcon from '@material-ui/icons/Launch';
import DeleteIcon from '@material-ui/icons/Delete';
import NotInterestedRoundedIcon from '@material-ui/icons/NotInterestedRounded';
import { ColoredChip } from '@components/colored-elements';
import BackspaceIcon from '@material-ui/icons/Backspace';
import { EntityIcon } from '@components/entity-type-icon';
import { AiOutlineInfoCircle, BsPencilSquare } from '@icon/react-icons';
import InputBase from '@material-ui/core/InputBase';
import { usePersonLoader } from 'services/person-dataloader.service';
import { Skeleton } from '@material-ui/lab';
import CategoryIcon from '@material-ui/icons/Category';
import LanguageIcon from '@material-ui/icons/Language';
import { useContentLoader } from 'services/content-dataloader.service';
import styled from 'styled-components';
import { thumbStack } from '@lib/common/css';
import AddToQueueIcon from '@material-ui/icons/AddToQueue';
import { useSnackbar } from 'notistack';
import { CreateEntityRequest } from './entity-request';
import Badge from '@material-ui/core/Badge';
import { withStyles } from '@material-ui/core/styles';
import { of } from 'rxjs';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { Link as Link1 } from 'react-router-dom';
import { DELI_ENV_BASE_URL, http_v3 } from '@api/client';
import { Autocomplete } from '@material-ui/lab';
import CircularProgress from '@material-ui/core/CircularProgress';
import { debounce } from 'lodash';

interface EntitySearchModel {
  vtr_id: string;
  type: string;
  name: string;
  relation_type: string;
  meta?: any;
  // Below are for clearing already existing data in partial entity
  imdb_id?: string;
  deal_id?: number | string;
  confidence?: number;
  deal_content_genre?: string;
  language?: string;
  country_of_origin?: string;
  deal_content_type?: string;
  book_name?: string;
  working_title?: string;
  deal_season?: string;
  original_title?: string;
  publish?: number;
  hqcountry?: string;
}

const useEntityStyle = makeStyles(theme =>
  createStyles({
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    disabledCard: {
      background: grey[100],
    },
    hoverEditEntity: {
      '&:hover $editEntity': {
        opacity: 1,
      },
    },
    editEntity: {
      opacity: 0,
    },
    inputBaseStyle: {
      fontSize: '1.5rem',
    },
  })
);

export function EntitySearchMultiPartyP2C({
  label,
  hideTypeSelect = false,
  clearable = false,
  defaultValue = { name: '', type: EntityType.person, vtr_id: '', relation_type: 'person' },
  onChange = null,
  changePerson = null,
  titleEditable = false,
  fixedType = '',
  indexOfEntities,
  removeDealEntities,
  isDealEntityRemovable = false,
  isDealEntityRequestCreatable = true,
  dealData = null,
  disabled = false,
  defaultSearchIn = 'name',
  stopInitialCall = false,
  personPublishable = false,
}) {
  const didMount = useRef(false);
  const [stopInitialCallRef, setStopInitialCallRef] = useState(stopInitialCall);
  const [expand, toggleExpanded] = useToggle(false);
  const [searchText, setSearchText] = useState(
    defaultSearchIn == 'vtr_id' ? defaultValue.vtr_id : defaultValue.name
  );
  const [type, setType] = useState(defaultValue.type);
  const [entityNameEditable, toggleEntityNameEditable] = useToggle(false);
  const classes = useEntityStyle();
  const resolvedType = fixedType || type || (EntityType.person as string);

  const [searchIn, setSearchIn] = useState(defaultSearchIn);

  const showSearchIn = resolvedType === EntityType.person || resolvedType === EntityType.content;

  const [searchTrigger, setSearchTrigger] = useState(0);
  const [tableReload, setTableReload] = useState(false);

  // Entity Values
  const [entity, setEntityValue] = useState<EntitySearchModel>(defaultValue);
  const resolvedTypeValue = fixedType || entity.type || EntityType.person;
  
  // Form Change.
  const setPartialEntity = useRecentInjector(
    [onChange, setEntityValue, entity] as const,
    ([onChange, setEntityValue, entity], value: Partial<EntitySearchModel>) => {
      const newValue = { ...entity, ...value };
      setEntityValue(newValue);
      onChange && onChange(newValue);
      changePerson && changePerson(newValue);
    }
  );
  
  const entityNameEl = useRef(null);

  const refreshTable = () => {
    setSearchText(searchText);
    setSearchTrigger(t => t + 1);
    resetPagination();
    setTableReload(true)
    toggleExpanded();
  }

  const { resetPagination, ...paginationProps } = useServerPagination(
    ({ page, pageSize }) => {
      if (!didMount.current && stopInitialCall) {
        didMount.current = true;
        return of({ data: [] });
      }
      setStopInitialCallRef(prev => false);
      
      if (resolvedType === EntityType.person) {
        return personService
          .personSearch({
            searchKey: searchIn === 'name' ? 'name' : 'vtr_id',
            query: searchText,
            page,
            pageSize,
          })
          .pipe(
            map(res => ({
              count: res.data.count,
              data: res.data.data.map(d => ({
                ...d,
                setPartialEntity,
                id: d.vtr_id,
                name: d.name,
              })),
            }))
          );
      }

      return pintService
        .search(resolvedType.toLowerCase() as any, {
          query: searchText,
          start: pageSize * (page - 1),
          limit: pageSize,
          ...(searchText && resolvedType === EntityType.content
            ? { sort: 'asc', sort_key: 'title' }
            : {}),
        })
        .pipe(
          map(res => ({
            data: (res.data[Object.keys(res.data)[0]] as Array<any>).map(d => ({
              ...d,
              setPartialEntity,
              id: d.vtr_id,
              name: d.name || d.title,
            })),
            count: typeof res._pages === 'string' ? 0 : res._pages.total,
          }))
        );
    }
  );

  const onTypeClick = () => {
    if (titleEditable) {
      toggleEntityNameEditable();
    }
  };

  const clearContent = () => {
    setEntityValue({
      name: '',
      type: resolvedTypeValue,
      vtr_id: '',
      relation_type: defaultValue.relation_type,
    });
    setSearchText('');
    onChange && onChange({
      name: '',
      type: resolvedTypeValue,
      vtr_id: '',
      relation_type: defaultValue.relation_type,
    });
  };

  const reSizeInputBase = () => {
    if (entityNameEl.current) {
      entityNameEl.current.style.width = `${entityNameEl.current.scrollWidth}px`;
    }
  };

  const freshLoadInputBaseToUpdateWidth = () => {
    setTimeout(() => {
      reSizeInputBase();
    }, 100);
  };

  useEffect(() => {
    freshLoadInputBaseToUpdateWidth();
  }, [entity.name]);

  return (
    <Card className={clsx(classes.hoverEditEntity, disabled && classes.disabledCard)}>
      <CardHeader
        action={
          <Grid container spacing={1} alignItems="center">
            {clearable && entity.name && (
              <Grid item>
                <Tooltip title="Clear">
                  <IconButton size="small" onClick={clearContent}>
                    <BackspaceIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {titleEditable && (
              <Grid item>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={onTypeClick}>
                    <BsPencilSquare />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {isDealEntityRemovable && (
              <Grid item>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    onClick={() => removeDealEntities(indexOfEntities)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        }
        title={
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <EntityIcon type={resolvedTypeValue} />
            </Grid>
            <Grid item>
              {entityNameEditable ? (
                <InputBase
                  ref={entityNameEl}
                  value={entity.name}
                  onChange={(e) => {
                    setEntityValue({ ...entity, name: e.target.value });
                    onChange && onChange({ ...entity, name: e.target.value });
                  }}
                  onBlur={() => toggleEntityNameEditable()}
                  className={classes.inputBaseStyle}
                  autoFocus
                />
              ) : (
                <Typography
                  variant="h6"
                  onClick={onTypeClick}
                  style={{ cursor: titleEditable ? 'pointer' : 'default' }}
                >
                  {entity.name || label || capitalCase(resolvedTypeValue)}
                </Typography>
              )}
            </Grid>
          </Grid>
        }
        subheader={
          <Grid container spacing={1} alignItems="center">
            {showSearchIn && (
              <Grid item>
                <FormControl size="small" variant="outlined">
                  <InputLabel>Search In</InputLabel>
                  <Select
                    value={searchIn}
                    onChange={(e) => setSearchIn(e.target.value as string)}
                    label="Search In"
                  >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="vtr_id">VTR ID</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item>
              <SearchBox
                label={`Search ${resolvedTypeValue}...`}
                onSearch={() => {
                  setSearchTrigger(t => t + 1);
                  resetPagination();
                }}
                defautlValue={searchText}
              />
            </Grid>
          </Grid>
        }
      />
      <Collapse in={expand} timeout="auto" unmountOnExit>
        <CardContent>
          <EntitySearchResult
            type={resolvedTypeValue}
            entity={entity}
            {...paginationProps}
            searchTrigger={searchTrigger}
            tableReload={tableReload}
          />
        </CardContent>
      </Collapse>
      <CardContent>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <IconButton
              className={clsx(classes.expand, {
                [classes.expandOpen]: expand,
              })}
              onClick={toggleExpanded}
              aria-expanded={expand}
              aria-label="show more"
            >
              <ExpandMoreIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              {entity.vtr_id ? `VTR ID: ${entity.vtr_id}` : 'No entity selected'}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function IdentifiedEntityChips({ entity, setEntityValue, type, isPerson = false}: { 
  entity: EntitySearchModel, 
  setEntityValue: any, 
  type: string, 
  isPerson: boolean
}) {
  return (
    <Grid container spacing={1}>
      <Grid item>
        <Chip
          label={`${capitalCase(type)}: ${entity.name}`}
          color="primary"
          variant="outlined"
        />
      </Grid>
      {entity.vtr_id && (
        <Grid item>
          <Chip
            label={`VTR ID: ${entity.vtr_id}`}
            color="secondary"
            variant="outlined"
          />
        </Grid>
      )}
      {isPerson && entity.imdb_id && (
        <Grid item>
          <Chip
            label={`IMDB: ${entity.imdb_id}`}
            color="default"
            variant="outlined"
          />
        </Grid>
      )}
    </Grid>
  );
}

function PersonInformation({ entity, setEntityValue, isPerson = false}: { 
  entity: EntitySearchModel, 
  setEntityValue: any, 
  isPerson: boolean
}) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <IdentifiedEntityChips entity={entity} setEntityValue={setEntityValue} type="person" isPerson={isPerson} />
      </Grid>
    </Grid>
  );
}

function ContentInformation({ entity }: { entity: EntitySearchModel }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <IdentifiedEntityChips entity={entity} setEntityValue={() => {}} type="content" isPerson={false} />
      </Grid>
    </Grid>
  );
}

type EntitySearchResultProps = Omit<DataGridProps, 'columns'> & {
  type: EntityType | `${EntityType}` | string;
  entity: any;
  searchTrigger?: number;
  tableReload?: boolean;
};

function EntitySearchResult({ type, searchTrigger, tableReload, entity, ...props }: EntitySearchResultProps) {
  const columns = useMemo(() => {
    if (type === EntityType.content) {
      return contentToColumns;
    }
    return [
      { field: 'name', headerName: 'Name', width: 200 },
      { field: 'vtr_id', headerName: 'VTR ID', width: 150 },
      { field: 'imdb_id', headerName: 'IMDB ID', width: 120 },
      { field: 'popularity', headerName: 'Popularity', width: 100 },
    ];
  }, [type]);

  return (
    <DataGrid
      {...props}
      columns={columns}
      loading={props.loading}
      rows={props.rows || []}
      pageSize={props.pageSize || 10}
      rowCount={props.rowCount || 0}
      pagination
      paginationMode="server"
      onPageChange={props.onPageChange}
      onPageSizeChange={props.onPageSizeChange}
      components={{
        NoRowsOverlay: () => (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography>No {type} found</Typography>
          </div>
        ),
      }}
    />
  );
} 